import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Table from "@cloudscape-design/components/table";
import Header from "@cloudscape-design/components/header";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Button from "@cloudscape-design/components/button";
import StatusIndicator from "@cloudscape-design/components/status-indicator";
import Badge from "@cloudscape-design/components/badge";
import Box from "@cloudscape-design/components/box";
import Tabs from "@cloudscape-design/components/tabs";
import PropertyFilter, {
  type PropertyFilterProps,
} from "@cloudscape-design/components/property-filter";
import { fetchSimtTickets } from "../api";
import type { Ticket, Severity } from "../types";

const SEVERITY_CONFIG: Record<
  Severity,
  { color: "error" | "warning" | "success" | "stopped"; label: string }
> = {
  SEV_2: { color: "error", label: "Sev-2" },
  SEV_3: { color: "warning", label: "Sev-3" },
  SEV_4: { color: "success", label: "Sev-4" },
  SEV_5: { color: "stopped", label: "Sev-5" },
};

function SeverityBadge({ severity }: { severity: Severity }) {
  const config = SEVERITY_CONFIG[severity];
  return <StatusIndicator type={config.color}>{config.label}</StatusIndicator>;
}

function StalenessIndicator({ days }: { days: number }) {
  if (days > 7) {
    return <StatusIndicator type="error">{days} days</StatusIndicator>;
  }
  if (days > 3) {
    return <StatusIndicator type="warning">{days} days</StatusIndicator>;
  }
  return <Box color="text-body-secondary">{days} days</Box>;
}

const FILTER_PROPERTIES: PropertyFilterProps.FilteringProperty[] = [
  {
    key: "severity",
    propertyLabel: "Severity",
    groupValuesLabel: "Severity values",
    operators: ["=", "!="],
  },
  {
    key: "status",
    propertyLabel: "Status",
    groupValuesLabel: "Status values",
    operators: ["=", "!="],
  },
  {
    key: "workflowStage",
    propertyLabel: "Stage",
    groupValuesLabel: "Stage values",
    operators: ["=", "!="],
  },
  {
    key: "assignee",
    propertyLabel: "Assignee",
    groupValuesLabel: "Assignee values",
    operators: ["=", "!="],
  },
  {
    key: "waitingOn",
    propertyLabel: "Waiting On",
    groupValuesLabel: "Waiting On values",
    operators: ["=", "!="],
  },
];

type TabId = "all" | "new" | "awaiting" | "monitoring" | "closeable";

export default function TicketListPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabId>("all");
  const [filterQuery, setFilterQuery] = useState<PropertyFilterProps.Query>({
    tokens: [],
    operation: "and",
  });

  const {
    data: tickets = [],
    isLoading,
    refetch,
    dataUpdatedAt,
  } = useQuery({
    queryKey: ["tickets"],
    queryFn: fetchSimtTickets,
  });

  // Filter tickets by tab
  const filteredByTab = tickets.filter((t) => {
    switch (activeTab) {
      case "new":
        return t.daysOpen <= 1;
      case "awaiting":
        return t.waitingOn !== "none";
      case "monitoring":
        return t.waitingOn === "none" && t.workflowStage !== "Resolved";
      case "closeable":
        return t.severity === "SEV_5" || t.severity === "SEV_4";
      default:
        return true;
    }
  });

  // Apply property filter
  const filteredTickets = filteredByTab.filter((ticket) => {
    if (filterQuery.tokens.length === 0) return true;

    return filterQuery.tokens.every((token) => {
      if (!("propertyKey" in token) || !token.propertyKey) return true;
      const key = token.propertyKey as keyof Ticket;
      const value = String(ticket[key]);
      if (token.operator === "=") return value === token.value;
      if (token.operator === "!=") return value !== token.value;
      return true;
    });
  });

  // Sort by severity (most severe first), then by days stale
  const sortedTickets = [...filteredTickets].sort((a, b) => {
    const sevOrder: Record<Severity, number> = { SEV_2: 0, SEV_3: 1, SEV_4: 2, SEV_5: 3 };
    const sevDiff = sevOrder[a.severity] - sevOrder[b.severity];
    if (sevDiff !== 0) return sevDiff;
    return b.daysStale - a.daysStale;
  });

  const lastSync = dataUpdatedAt
    ? `Last synced ${Math.round((Date.now() - dataUpdatedAt) / 60000)} min ago`
    : "";

  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        description={lastSync}
        counter={`(${sortedTickets.length})`}
        actions={
          <Button iconName="refresh" onClick={() => refetch()}>
            Refresh
          </Button>
        }
      >
        PIRT Triage Queue
      </Header>

      <Tabs
        activeTabId={activeTab}
        onChange={({ detail }) => setActiveTab(detail.activeTabId as TabId)}
        tabs={[
          {
            id: "all",
            label: `All (${tickets.length})`,
          },
          {
            id: "new",
            label: `New (${tickets.filter((t) => t.daysOpen <= 1).length})`,
          },
          {
            id: "awaiting",
            label: `Awaiting Response (${tickets.filter((t) => t.waitingOn !== "none").length})`,
          },
          {
            id: "monitoring",
            label: `Monitoring (${tickets.filter((t) => t.waitingOn === "none" && t.workflowStage !== "Resolved").length})`,
          },
          {
            id: "closeable",
            label: `Closeable (${tickets.filter((t) => t.severity === "SEV_5" || t.severity === "SEV_4").length})`,
          },
        ]}
      />

      <PropertyFilter
        query={filterQuery}
        onChange={({ detail }) => setFilterQuery(detail)}
        filteringProperties={FILTER_PROPERTIES}
        filteringPlaceholder="Filter tickets..."
        filteringEmpty="No matches"
      />

      <Table
        columnDefinitions={[
          {
            id: "severity",
            header: "Sev",
            cell: (item) => <SeverityBadge severity={item.severity} />,
            sortingField: "severity",
            width: 80,
          },
          {
            id: "ticketId",
            header: "Ticket ID",
            cell: (item) => (
              <span style={{ whiteSpace: "nowrap" }}>
                <Button variant="link" onClick={() => navigate(`/tickets/${item.ticketId}`)}>
                  {item.ticketId}
                </Button>
              </span>
            ),
            sortingField: "ticketId",
            minWidth: 180,
          },
          {
            id: "title",
            header: "Title",
            cell: (item) => (
              <SpaceBetween direction="horizontal" size="xs">
                <span>{item.title}</span>
                {item.isEu && <Badge color="blue">EU</Badge>}
              </SpaceBetween>
            ),
            sortingField: "title",
          },
          {
            id: "waitingOn",
            header: "Waiting On",
            cell: (item) =>
              item.waitingOn !== "none" ? (
                <Badge color="grey">{item.waitingOn}</Badge>
              ) : (
                "—"
              ),
            sortingField: "waitingOn",
            width: 110,
          },
          {
            id: "daysStale",
            header: "Stale",
            cell: (item) => <StalenessIndicator days={item.daysStale} />,
            sortingField: "daysStale",
            width: 80,
          },
          {
            id: "assignee",
            header: "Assignee",
            cell: (item) => item.assignee,
            sortingField: "assignee",
            width: 100,
          },
          {
            id: "workflowStage",
            header: "Stage",
            cell: (item) => item.workflowStage,
            sortingField: "workflowStage",
            width: 120,
          },
          {
            id: "daysOpen",
            header: "Age",
            cell: (item) => `${item.daysOpen}d`,
            sortingField: "daysOpen",
            width: 50,
          },
        ]}
        items={sortedTickets}
        loading={isLoading}
        loadingText="Loading tickets..."
        sortingDisabled={false}
        variant="full-page"
        stickyHeader={true}
        empty={
          <Box textAlign="center" color="inherit">
            <b>No tickets</b>
            <Box padding={{ bottom: "s" }} variant="p" color="inherit">
              No open tickets found in the queue.
            </Box>
          </Box>
        }
      />
    </SpaceBetween>
  );
}
